import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import { IoArrowBack, IoAlertCircleOutline, IoChevronDown, IoSearch, IoTrashOutline, IoLockClosedOutline } from 'react-icons/io5';
import './CreateTasksFromTemplatePage.css';

// --- Configuration & API ---
const SWAGGER_BASE_URL = process.env.REACT_APP_SWAGGER_SERVICE_BASE_URL;
const NODE_BASE_URL = process.env.REACT_APP_NODE_SERVICE_BASE_URL;
const ASSET_BASE_URL = process.env.REACT_APP_ASSET_SERVICE_BASE_URL;
const AUTH_TOKEN_KEY = 'zTaskAuthToken';

const getApiHeaders = () => ({ 'Authorization': `Bearer ${localStorage.getItem(AUTH_TOKEN_KEY)}`, 'Content-Type': 'application/json', 'Accept': 'application/json' });

const apiFetch = async (url, options) => {
    const response = await fetch(url, options);
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Request failed with status ${response.status}`);
    }
    return response.json();
};

const getUsers = async () => { const data = await apiFetch(`${SWAGGER_BASE_URL}/api/get-users`, { headers: getApiHeaders() }); return data.userData || []; };
const createTask = async (payload) => apiFetch(`${SWAGGER_BASE_URL}/api/create-task`, { method: 'POST', headers: getApiHeaders(), body: JSON.stringify(payload) });
const createDraftTask = async (payload) => apiFetch(`${SWAGGER_BASE_URL}/api/create-draft-task`, { method: 'POST', headers: getApiHeaders(), body: JSON.stringify(payload) });
const getProjects = async () => { const data = await apiFetch(`${SWAGGER_BASE_URL}/api/get-project`, { headers: getApiHeaders() }); return data.projects; };
const getProjectsByCustomerId = async (customerId) => apiFetch(`${SWAGGER_BASE_URL}/api/get-projects-by-customer-id?customerId=${customerId}`, { headers: getApiHeaders() });
const getCustomers = async () => apiFetch(`${NODE_BASE_URL}/customer/api/customers/`, { headers: getApiHeaders() });
const getAssets = async () => apiFetch(`${ASSET_BASE_URL}/api/assets/`, { headers: getApiHeaders() });
const getColumnData = async (table, column) => apiFetch(`${NODE_BASE_URL}/api/get-column/${table}/${column}`, { headers: getApiHeaders() });
const getReportingManagers = async (userId) => { const data = await apiFetch(`${SWAGGER_BASE_URL}/api/get-reporting-managers-by-userId?userId=${userId}`, { headers: getApiHeaders() }); return data.message || []; };
const getProjectTemplateById = async (templateId) => { const data = await apiFetch(`${SWAGGER_BASE_URL}/api/get-Project Template-by-id?projectTemplateId=${templateId}`, { headers: getApiHeaders() }); return data.project_template; };
const getFullHierarchyTree = async () => apiFetch(`${SWAGGER_BASE_URL}/api-get-full-hierarchy-tree`, { headers: getApiHeaders() });
const getDraftsByTemplateId = async (templateId) => { try { const data = await apiFetch(`${SWAGGER_BASE_URL}/api/get-all-draft-task-by-template-id?templateId=${templateId}`, { headers: getApiHeaders() }); return data; } catch (error) { if (error.message.includes('404') || error.message.includes('400')) { return { draft_tasks: [] }; } throw error; } };

const getStatuses = () => getColumnData('common_master', 'current_status');
const getPriorities = () => getColumnData('common_master', 'priority_in_daily_task');
const getCategories = () => getColumnData('common_master', 'category');

// --- Constants & Helper Functions ---
const pad = (num) => num.toString().padStart(2, '0');
const currentYear = new Date().getFullYear();
const years = Array.from({ length: 101 }, (_, i) => currentYear - 50 + i);
const allMonths = [ { value: '1', name: 'Jan' }, { value: '2', name: 'Feb' }, { value: '3', name: 'Mar' }, { value: '4', name: 'Apr' }, { value: '5', name: 'May' }, { value: '6', name: 'Jun' }, { value: '7', name: 'Jul' }, { value: '8', name: 'Aug' }, { value: '9', name: 'Sep' }, { value: '10', name: 'Oct' }, { value: '11', name: 'Nov' }, { value: '12', name: 'Dec' } ];
const getDaysInMonth = (year, month) => new Date(year, month, 0).getDate();
const allHours = Array.from({ length: 24 }, (_, i) => pad(i));
const allMinutes = Array.from({ length: 60 }, (_, i) => pad(i));
const sortAlphabetically = (array, key) => { if (!Array.isArray(array)) return []; return [...array].sort((a, b) => (String(a[key] || '').toLowerCase()).localeCompare(String(b[key] || '').toLowerCase())); };
const findUserInHierarchy = (tree, userName) => { for (const node of tree) { if (node.name === userName) return node; if (node.children) { const found = findUserInHierarchy(node.children, userName); if (found) return found; } } return null; };
const getAllSubordinates = (userNode) => { let subs = []; if (userNode?.children) { for (const child of userNode.children) { subs.push(child); subs = subs.concat(getAllSubordinates(child)); } } return subs; };

// --- Reusable Sub-Components ---
const CreateTaskPageSkeleton = () => ( <div className="create-task-page"><div className="page-header"><div className="shimmer-line back-link-skeleton"></div><div className="shimmer-line title-skeleton"></div><div className="shimmer-line subtitle-skeleton"></div></div><div className="create-task-form"><div className="form-main-panel"><div className="shimmer-line section-header-skeleton"></div>{[...Array(6)].map((_, i) => (<div key={i} className="form-group-skeleton"><div className="shimmer-line label-skeleton"></div><div className="shimmer-block input-skeleton"></div></div>))}<div className="form-group-skeleton"><div className="shimmer-line label-skeleton"></div><div className="shimmer-block textarea-skeleton"></div></div></div><div className="form-side-panel"><div className="shimmer-line section-header-skeleton"></div>{[...Array(4)].map((_, i) => (<div key={i} className="form-group-skeleton"><div className="shimmer-line label-skeleton"></div><div className="shimmer-block input-skeleton"></div></div>))}</div></div></div> );
const AccessDenied = () => ( <div className="create-task-page"><div className="page-header"><Link to="/tasks" className="back-link"><IoArrowBack /> Back to Tasks</Link><h1>Access Denied</h1></div><div className="error-message icon-flex" role="alert" style={{ marginTop: '20px' }}><IoLockClosedOutline size={24} style={{ marginRight: '10px' }} />You do not have permission to perform this action.</div></div> );
const useOutsideAlerter = (ref, callback) => { useEffect(() => { const handleClickOutside = (event) => { if (ref.current && !ref.current.contains(event.target)) { callback(); } }; document.addEventListener('mousedown', handleClickOutside); return () => document.removeEventListener('mousedown', handleClickOutside); }, [ref, callback]); };

const SearchableDropdown = ({ options, selectedValues, onChange, placeholder, isMultiSelect = false, labelField = 'name', valueField = 'id', disabled = false, error = false, dataLoadError = null, allowClear = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);
    useOutsideAlerter(dropdownRef, () => setIsOpen(false));

    const filteredOptions = useMemo(() => options.filter(option => option && typeof option[labelField] === 'string' ? option[labelField].toLowerCase().includes(searchTerm.toLowerCase()) : false), [options, searchTerm, labelField]);
    const handleSelect = (optionValue) => { if (isMultiSelect) { const newValues = selectedValues.includes(optionValue) ? selectedValues.filter(v => v !== optionValue) : [...selectedValues, optionValue]; onChange(newValues); } else { onChange(optionValue); setIsOpen(false); } };
    
    const getDisplayValue = () => {
        if (isMultiSelect) {
            if (selectedValues.length === 0) return placeholder;
            if (selectedValues.length === 1) { const selectedOption = options.find(opt => opt[valueField] === selectedValues[0]); return selectedOption ? selectedOption[labelField] : '1 selected'; }
            return `${selectedValues.length} items selected`;
        } else {
            const selectedOption = options.find(opt => String(opt[valueField]) === String(selectedValues));
            return selectedOption ? selectedOption[labelField] : placeholder;
        }
    };
    
    return (
        <div className="searchable-dropdown-container" ref={dropdownRef}>
            <button type="button" className={`dropdown-trigger ${error ? 'invalid-field' : ''}`} onClick={() => !disabled && setIsOpen(!isOpen)} disabled={disabled}>
                <span>{getDisplayValue()}</span>
                <IoChevronDown className={`dropdown-chevron ${isOpen ? 'open' : ''}`} />
            </button>
            {isOpen && (
                <div className="dropdown-panel">
                    <div className="dropdown-search-container">
                        <input type="text" className="dropdown-search-input" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} autoFocus />
                        <IoSearch className="search-icon" />
                    </div>
                    <ul className="dropdown-options-list">
                        {dataLoadError && <li className="dropdown-option-message">{dataLoadError}</li>}
                        {!dataLoadError && allowClear && !isMultiSelect && (
                            <li className="dropdown-option clear-option" onClick={() => handleSelect('')}>
                                — Clear Selection —
                            </li>
                        )}
                        {!dataLoadError && filteredOptions.length > 0 ? (
                            filteredOptions.map(option => {
                                const isSelected = isMultiSelect ? selectedValues.includes(option[valueField]) : String(selectedValues) === String(option[valueField]);
                                return (
                                    <li key={option[valueField]} className={`dropdown-option ${isSelected ? 'selected' : ''}`} onClick={() => handleSelect(option[valueField])}>
                                        {isMultiSelect && (
                                            <input type="checkbox" checked={isSelected} readOnly className="dropdown-checkbox" />
                                        )}
                                        {option[labelField]}
                                    </li>
                                );
                            })
                        ) : (
                            !dataLoadError && <li className="dropdown-option-message">No results found</li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};

// --- Main Component ---
const CreateTasksFromTemplatePage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, hasPermission } = usePermissions();
    const { templateIdToLoad, isEditingDraft } = location.state || {};

    // --- State ---
    const [mainTasks, setMainTasks] = useState([]);
    const [templateName, setTemplateName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSavingDraft, setIsSavingDraft] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    
    // Loading state flags
    const [isFetchingTemplate, setIsFetchingTemplate] = useState(true);
    const [isFetchingMasterData, setIsFetchingMasterData] = useState(true);

    // Master data state
    const [allUsers, setAllUsers] = useState([]);
    const [assigneeOptions, setAssigneeOptions] = useState([]);
    const [allProjects, setAllProjects] = useState([]);
    const [allCustomers, setAllCustomers] = useState([]);
    const [allAssets, setAllAssets] = useState([]);
    const [allStatuses, setAllStatuses] = useState([]);
    const [allPriorities, setAllPriorities] = useState([]);
    const [allCategories, setAllCategories] = useState([]);

    // --- OPTIMIZED DATA LOADING ---

    // Effect 1: Fetch core template/draft structure first for immediate render
    useEffect(() => {
        if (!templateIdToLoad) {
            setError("No template was selected.");
            setIsFetchingTemplate(false);
            return;
        }

        const loadInitialStructure = async () => {
            try {
                const [template, draftData] = await Promise.all([
                    getProjectTemplateById(templateIdToLoad),
                    isEditingDraft ? getDraftsByTemplateId(templateIdToLoad) : Promise.resolve({ draft_tasks: [] })
                ]);

                setTemplateName(template.projectTemplateName);
                const drafts = draftData.draft_tasks || [];
                const draftQueue = [...drafts];

                const structuredTasks = template.mainTasks.map(mainTaskTemplate => {
                    const draft = draftQueue.shift() || {}; // Use empty object if no draft
                    return {
                        id: mainTaskTemplate.mainTaskId,
                        title: draft.title || mainTaskTemplate.mainTaskTitle,
                        description: draft.description || '',
                        dueDate: draft.dueDate ? new Date(draft.dueDate) : new Date(),
                        status: draft.status ? draft.status.charAt(0).toUpperCase() + draft.status.slice(1) : 'Open',
                        priority: draft.priority || 'Medium',
                        category: draft.category || '',
                        _draft: draft,
                        assigneeId: '', reportingManagerId: '', projectId: '', customerId: '', assetIds: [], taggedUserIds: [],
                        reportingManagers: [], invalidFields: [],
                        // NEW: State for dependent project dropdown
                        projectOptions: [], 
                        isFetchingProjects: false,
                        subTasks: mainTaskTemplate.subTasks.map(subTaskTemplate => {
                            const subDraft = draftQueue.shift() || {};
                            return {
                                id: subTaskTemplate.subTaskId,
                                title: subDraft.title || subTaskTemplate.subTaskTitle,
                                description: subDraft.description || '',
                                dueDate: subDraft.dueDate ? new Date(subDraft.dueDate) : new Date(),
                                status: subDraft.status ? subDraft.status.charAt(0).toUpperCase() + subDraft.status.slice(1) : 'Open',
                                priority: subDraft.priority || 'Medium',
                                _draft: subDraft,
                                assigneeId: '', reportingManagerId: '', taggedUserIds: [],
                                reportingManagers: [], invalidFields: [],
                            };
                        })
                    };
                });
                
                setMainTasks(structuredTasks);
            } catch (err) {
                setError(`Failed to load template structure: ${err.message}`);
            } finally {
                setIsFetchingTemplate(false);
            }
        };

        loadInitialStructure();
    }, [templateIdToLoad, isEditingDraft]);

    // Effect 2: Fetch all master data in the background
    useEffect(() => {
        const fetchMasterData = async () => {
            try {
                const [users, projects, customers, statuses, priorities, categories, assets, hierarchyData] = await Promise.all([
                    getUsers(), getProjects(), getCustomers(), getStatuses(), getPriorities(), getCategories(), getAssets(), getFullHierarchyTree()
                ]);

                // Set master data states
                const activeUsers = users.filter(u => u.active);
                setAllUsers(sortAlphabetically(activeUsers, 'name').map(u => ({...u, id: String(u.id)})));
                const sortedProjects = sortAlphabetically(projects, 'projectName').map(p => ({ ...p, id: p.projectId, name: p.projectName }))
                setAllProjects(sortedProjects);
                setAllCustomers(sortAlphabetically(customers.filter(c => c.active), 'name').map(c => ({...c, id: String(c.id)})));
                setAllStatuses(statuses.map(s => ({ id: s.value, name: s.value.replace(/_/g, ' ') })));
                setAllPriorities(priorities.map(p => ({ id: p.value, name: p.value })));
                setAllCategories(categories.map(c => ({ id: c.value, name: c.value })));
                setAllAssets(sortAlphabetically(assets, 'name').map(a => ({ ...a, id: a.id, name: a.name })));

                // NEW: Populate initial project options for all tasks
                setMainTasks(prevTasks => prevTasks.map(task => ({
                    ...task,
                    projectOptions: sortedProjects
                })));

                if (user) {
                    const userRole = user?.role?.name.toUpperCase();
                    if (userRole === 'SUPER_ADMIN') {
                        setAssigneeOptions(activeUsers);
                    } else {
                        const tree = hierarchyData?.tree || [];
                        const currentUserNode = findUserInHierarchy(tree, user.name);
                        if (currentUserNode) {
                            const subordinateUsers = getAllSubordinates(currentUserNode);
                            const subordinateIds = new Set(subordinateUsers.map(u => String(u.id)));
                            const options = activeUsers.filter(u => String(u.id) === String(user.id) || subordinateIds.has(String(u.id)));
                            setAssigneeOptions(options);
                        } else {
                            setAssigneeOptions(activeUsers.filter(u => String(u.id) === String(user.id)));
                        }
                    }
                }
            } catch (err) {
                setError(`Failed to load selection data: ${err.message}`);
            } finally {
                setIsFetchingMasterData(false);
            }
        };

        fetchMasterData();
    }, [user]);

    // Effect 3: Hydrate the form with IDs once master data is ready
    useEffect(() => {
        if (isFetchingMasterData || isFetchingTemplate || !isEditingDraft || mainTasks.length === 0) return;

        const hydrateFormWithDraftData = async () => {
            const assigneeIdsToFetchManagers = new Set();
            
            const hydratedTasks = mainTasks.map(task => {
                const draft = task._draft;
                const assignee = allUsers.find(u => u.name === draft?.assigneeName);
                const project = allProjects.find(p => p.name === draft?.projectName);
                if (assignee) assigneeIdsToFetchManagers.add(assignee.id);

                return {
                    ...task,
                    assigneeId: assignee?.id || '',
                    projectId: project?.id || '',
                    customerId: draft?.customerId ? String(draft.customerId) : '',
                    subTasks: task.subTasks.map(subtask => {
                        const subDraft = subtask._draft;
                        const subAssignee = allUsers.find(u => u.name === subDraft?.assigneeName);
                        if (subAssignee) assigneeIdsToFetchManagers.add(subAssignee.id);
                        return {
                            ...subtask,
                            assigneeId: subAssignee?.id || '',
                        };
                    })
                };
            });
            
            setMainTasks(hydratedTasks);

            // Fetch managers for all unique assignees in parallel
            try {
                const managerPromises = Array.from(assigneeIdsToFetchManagers).map(id => getReportingManagers(id));
                const managerResults = await Promise.all(managerPromises);
                
                const managersMap = new Map();
                Array.from(assigneeIdsToFetchManagers).forEach((id, index) => {
                    const self = allUsers.find(a => String(a.id) === String(id));
                    let formattedManagers = (managerResults[index] || []).map(m => allUsers.find(u => u.email === m.managerEmail)).filter(Boolean).map(u => ({ id: String(u.id), name: u.name }));
                    if (formattedManagers.length === 0 && self) formattedManagers.push({ id: String(self.id), name: self.name });
                    managersMap.set(id, sortAlphabetically(formattedManagers, 'name'));
                });

                // Final update with manager lists and selected manager IDs
                setMainTasks(currentTasks => currentTasks.map(task => ({
                    ...task,
                    reportingManagers: managersMap.get(task.assigneeId) || [],
                    reportingManagerId: allUsers.find(u => String(u.id) === String(task._draft.reportingManagerRMID))?.id || '',
                    subTasks: task.subTasks.map(subtask => ({
                        ...subtask,
                        reportingManagers: managersMap.get(subtask.assigneeId) || [],
                        reportingManagerId: allUsers.find(u => String(u.id) === String(subtask._draft.reportingManagerRMID))?.id || '',
                    }))
                })));

            } catch(err) {
                console.error("Failed to fetch reporting managers during hydration:", err);
            }
        };

        hydrateFormWithDraftData();

    }, [isFetchingMasterData, isFetchingTemplate, mainTasks.length]);


    // --- Event Handlers ---
    const handleTaskChange = (mainTaskId, field, value) => {
        setMainTasks(prev => prev.map(mt => {
            if (mt.id === mainTaskId) {
                const updatedTask = { ...mt, [field]: value, invalidFields: mt.invalidFields.filter(f => f !== field) };
                if (field === 'assigneeId') {
                    updatedTask.reportingManagerId = ''; 
                    fetchManagersForTask(mainTaskId, null, value);
                    if(updatedTask.taggedUserIds.includes(value)) {
                       updatedTask.taggedUserIds = updatedTask.taggedUserIds.filter(id => id !== value);
                    }
                }
                return updatedTask;
            }
            return mt;
        }));
    };

    // NEW: Handler for customer change to trigger project filtering
    const handleCustomerChange = async (mainTaskId, customerId) => {
        // Update the customer ID and clear the project ID for the specific task
        setMainTasks(prev => prev.map(mt => {
            if (mt.id === mainTaskId) {
                return { ...mt, customerId, projectId: '', isFetchingProjects: true, invalidFields: mt.invalidFields.filter(f => f !== 'customerId') };
            }
            return mt;
        }));

        let projectsForTask = allProjects;
        if (customerId) {
            try {
                const data = await getProjectsByCustomerId(customerId);
                projectsForTask = (data.projects || []).map(p => ({ ...p, id: p.projectId, name: p.projectName }));
            } catch (error) {
                console.error(`Failed to fetch projects for customer ${customerId}:`, error);
                projectsForTask = []; // Or handle error appropriately
            }
        }

        // Update the project options for the specific task
        setMainTasks(prev => prev.map(mt => {
            if (mt.id === mainTaskId) {
                return { ...mt, projectOptions: sortAlphabetically(projectsForTask, 'name'), isFetchingProjects: false };
            }
            return mt;
        }));
    };

    const handleSubtaskChange = (mainTaskId, subTaskId, field, value) => {
        setMainTasks(prev => prev.map(mt => {
            if (mt.id === mainTaskId) {
                return {
                    ...mt,
                    subTasks: mt.subTasks.map(st => {
                        if (st.id === subTaskId) {
                            const updatedSubtask = { ...st, [field]: value, invalidFields: st.invalidFields.filter(f => f !== field) };
                             if (field === 'assigneeId') {
                                updatedSubtask.reportingManagerId = '';
                                fetchManagersForTask(mainTaskId, subTaskId, value);
                                if(updatedSubtask.taggedUserIds.includes(value)) {
                                    updatedSubtask.taggedUserIds = updatedSubtask.taggedUserIds.filter(id => id !== value);
                                }
                            }
                            return updatedSubtask;
                        }
                        return st;
                    })
                };
            }
            return mt;
        }));
    };

    const handleDateTimeChange = (mainTaskId, subTaskId, part, value) => {
        setMainTasks(prev => prev.map(mt => {
            if (mt.id === mainTaskId) {
                if (subTaskId) {
                    return { ...mt, subTasks: mt.subTasks.map(st => { if (st.id === subTaskId) { const newDate = new Date(st.dueDate); switch(part) { case 'day': newDate.setDate(parseInt(value, 10)); break; case 'month': newDate.setMonth(parseInt(value, 10) - 1); break; case 'year': newDate.setFullYear(parseInt(value, 10)); break; case 'hour': newDate.setHours(parseInt(value, 10)); break; case 'minute': newDate.setMinutes(parseInt(value, 10)); break; default: break; } return { ...st, dueDate: newDate > mt.dueDate ? mt.dueDate : newDate }; } return st; }) };
                } else {
                    const newDate = new Date(mt.dueDate); switch(part) { case 'day': newDate.setDate(parseInt(value, 10)); break; case 'month': newDate.setMonth(parseInt(value, 10) - 1); break; case 'year': newDate.setFullYear(parseInt(value, 10)); break; case 'hour': newDate.setHours(parseInt(value, 10)); break; case 'minute': newDate.setMinutes(parseInt(value, 10)); break; default: break; }
                    const updatedSubtasks = mt.subTasks.map(st => ({ ...st, dueDate: st.dueDate > newDate ? newDate : st.dueDate }));
                    return { ...mt, dueDate: newDate, subTasks: updatedSubtasks };
                }
            }
            return mt;
        }));
    };

    const fetchManagersForTask = async (mainTaskId, subTaskId, assigneeId) => {
        if (!assigneeId) return;
        try {
            const managersData = await getReportingManagers(assigneeId);
            const self = allUsers.find(a => String(a.id) === String(assigneeId));
            let formattedManagers = (managersData || []).map(m => allUsers.find(u => u.email === m.managerEmail)).filter(Boolean).map(u => ({ id: String(u.id), name: u.name }));
            if (formattedManagers.length === 0 && self) formattedManagers.push({ id: String(self.id), name: self.name });
            
            setMainTasks(prev => prev.map(mt => {
                if (mt.id === mainTaskId) {
                    const sortedManagers = sortAlphabetically(formattedManagers, 'name');
                    if (subTaskId) {
                        return { ...mt, subTasks: mt.subTasks.map(st => st.id === subTaskId ? { ...st, reportingManagers: sortedManagers } : st) };
                    } else {
                        return { ...mt, reportingManagers: sortedManagers };
                    }
                }
                return mt;
            }));
        } catch (err) { console.error(`Failed to fetch managers:`, err); }
    };

    const handleSaveAsDraft = async () => {
        setIsSavingDraft(true);
        setError(null);
        setSuccessMessage('');

        // Minimal validation for draft: title must exist
        let isValid = true;
        const tasksWithValidation = mainTasks.map(mt => {
            let invalidFields = mt.invalidFields.filter(f => f !== 'title');
            if (!mt.title.trim()) {
                isValid = false;
                invalidFields.push('title');
            }
            const updatedSubtasks = mt.subTasks.map(st => {
                let subInvalidFields = st.invalidFields.filter(f => f !== 'title');
                if (!st.title.trim()) {
                    isValid = false;
                    subInvalidFields.push('title');
                }
                return { ...st, invalidFields: subInvalidFields };
            });
            return { ...mt, invalidFields, subTasks: updatedSubtasks };
        });

        if (!isValid) {
            setMainTasks(tasksWithValidation);
            setError("All tasks and subtasks must have a title to be saved as a draft.");
            setIsSavingDraft(false);
            return;
        }

        const draftPayload = [];
        mainTasks.forEach(task => {
            const assignee = allUsers.find(u => String(u.id) === String(task.assigneeId));
            const project = allProjects.find(p => String(p.id) === String(task.projectId));
            const assetNames = task.assetIds.map(id => allAssets.find(a => String(a.id) === String(id))?.name).filter(Boolean);

            draftPayload.push({
                parentTaskId: 0,
                title: task.title,
                description: task.description || "",
                status: task.status,
                priority: task.priority,
                assigneeName: assignee ? assignee.name : "",
                dueDate: task.dueDate ? new Date(task.dueDate).toISOString() : new Date().toISOString(),
                category: task.category || "",
                projectName: project ? project.name : "",
                comments: "",
                remarks: "",
                assetName: assetNames,
                taggedUsers: task.taggedUserIds.map(id => Number(id)),
                customerId: task.customerId ? Number(task.customerId) : null,
                reminderDays: 0,
                reportingManagerId: task.reportingManagerId ? Number(task.reportingManagerId) : null,
                active: true,
                no_of_sub_tasks: task.subTasks.length,
                templateId: templateIdToLoad ? Number(templateIdToLoad) : null,
                templateUsed: true,
            });

            task.subTasks.forEach(subtask => {
                const subAssignee = allUsers.find(u => String(u.id) === String(subtask.assigneeId));
                draftPayload.push({
                    parentTaskId: 0, // In draft mode, all tasks are sent as a flat list
                    title: subtask.title,
                    description: subtask.description || "",
                    status: subtask.status,
                    priority: subtask.priority,
                    assigneeName: subAssignee ? subAssignee.name : "",
                    dueDate: subtask.dueDate ? new Date(subtask.dueDate).toISOString() : new Date().toISOString(),
                    category: task.category || "",
                    projectName: project ? project.name : "",
                    comments: "",
                    remarks: "",
                    assetName: [],
                    taggedUsers: subtask.taggedUserIds.map(id => Number(id)),
                    customerId: task.customerId ? Number(task.customerId) : null,
                    reminderDays: 0,
                    reportingManagerId: subtask.reportingManagerId ? Number(subtask.reportingManagerId) : null,
                    active: true,
                    no_of_sub_tasks: 0,
                    templateId: templateIdToLoad ? Number(templateIdToLoad) : null,
                    templateUsed: true,
                });
            });
        });

        try {
            await createDraftTask(draftPayload);
            setSuccessMessage("Draft saved successfully! You will be redirected shortly.");
            setTimeout(() => navigate('/tasks'), 2000);
        } catch (err) {
            setError(err.message || "An unexpected error occurred while saving the draft.");
        } finally {
            setIsSavingDraft(false);
        }
    };

    const handleSubmit = async () => {
        setError(null);
        let isValid = true;
        let firstError = null;

        const tasksWithValidation = mainTasks.map(mt => {
            let invalidFields = [];
            if (!mt.title.trim()) { isValid = false; invalidFields.push('title'); if (!firstError) firstError = "Title is required."; }
            if (!mt.assigneeId) { isValid = false; invalidFields.push('assigneeId'); if (!firstError) firstError = "Assignee is required."; }
            if (!mt.reportingManagerId) { isValid = false; invalidFields.push('reportingManagerId'); if (!firstError) firstError = "Reporting Manager is required."; }
            
            const updatedSubtasks = mt.subTasks.map(st => {
                let subInvalidFields = [];
                if (!st.title.trim()) { isValid = false; subInvalidFields.push('title'); if (!firstError) firstError = "Subtask title is required."; }
                if (!st.assigneeId) { isValid = false; subInvalidFields.push('assigneeId'); if (!firstError) firstError = "Subtask assignee is required."; }
                if (!st.reportingManagerId) { isValid = false; subInvalidFields.push('reportingManagerId'); if (!firstError) firstError = "Subtask reporting manager is required."; }
                return { ...st, invalidFields: subInvalidFields };
            });
            return { ...mt, invalidFields, subTasks: updatedSubtasks };
        });

        if (!isValid) {
            setMainTasks(tasksWithValidation);
            setError(`Please fill all required fields. ${firstError}`);
            return;
        }

        setIsSubmitting(true);
        setSuccessMessage('');
        
        try {
            for (const task of mainTasks) {
                const mainTaskPayload = {
                    parentTaskId: 0,
                    title: task.title,
                    description: task.description || "",
                    status: task.status,
                    priority: task.priority,
                    assigneeId: Number(task.assigneeId),
                    dueDate: new Date(task.dueDate).toISOString(),
                    category: task.category || null,
                    projectId: task.projectId ? Number(task.projectId) : null,
                    comments: [],
                    remarks: "",
                    assetIds: task.assetIds.map(id => Number(id)),
                    taggedUserIds: task.taggedUserIds.map(id => Number(id)),
                    customerId: task.customerId ? Number(task.customerId) : null,
                    reportingManagerId: Number(task.reportingManagerId),
                };
                
                const createdMainTask = await createTask(mainTaskPayload);

                if (createdMainTask && createdMainTask.taskId && task.subTasks.length > 0) {
                    for (const subtask of task.subTasks) {
                        const subTaskPayload = {
                            parentTaskId: createdMainTask.taskId,
                            title: subtask.title,
                            description: subtask.description || "",
                            status: subtask.status,
                            priority: subtask.priority,
                            assigneeId: Number(subtask.assigneeId),
                            dueDate: new Date(subtask.dueDate).toISOString(),
                            category: task.category || null,
                            projectId: task.projectId ? Number(task.projectId) : null,
                            comments: [],
                            remarks: "",
                            assetIds: [],
                            taggedUserIds: subtask.taggedUserIds.map(id => Number(id)),
                            customerId: task.customerId ? Number(task.customerId) : null,
                            reportingManagerId: Number(subtask.reportingManagerId),
                        };
                        await createTask(subTaskPayload);
                    }
                }
            }
            setSuccessMessage("All tasks created successfully! You will be redirected shortly.");
            setTimeout(() => navigate('/tasks'), 2000);
        } catch (err) {
            setError(err.message || "An unexpected error occurred while creating tasks.");
        } finally {
            setIsSubmitting(false);
        }
    };
    
    if (isFetchingTemplate || isFetchingMasterData) return <CreateTaskPageSkeleton />;
    if (!hasPermission('CREATE TASK')) return <AccessDenied />;

    return (
        <div className="create-task-page">
            <div className="page-header">
                <Link to="/tasks" className="back-link"><IoArrowBack /> Back to Tasks</Link>
                <h1>{isEditingDraft ? 'Edit Draft Tasks from' : 'Create Tasks from'} Template: {templateName}</h1>
                <p>{isEditingDraft ? 'Update the details below and save or create the tasks.' : 'Fill in the details below to generate the full task structure.'}</p>
            </div>
            
            {successMessage && <div className="success-message" role="alert">{successMessage}</div>}
            {error && <div className="error-message icon-flex" role="alert"><IoAlertCircleOutline size={20} />{error}</div>}

            <div className="create-task-form" style={{ display: 'flex', flexDirection: 'column' }}>
                {mainTasks.map((mainTask, index) => {
                    const mainTaskDate = new Date(mainTask.dueDate);
                    const taggableUsersForMain = allUsers.filter(u => String(u.id) !== String(mainTask.assigneeId));
                    const areDropdownsDisabled = isFetchingMasterData;

                    return (
                    <div key={mainTask.id} className="template-main-task-section">
                        <h3>Main Task #{index + 1}</h3>
                        <div className="create-task-form">
                            <div className="form-main-panel">
                                <div className="form-group"><label>Title <span className="required-asterisk">*</span></label><input type="text" value={mainTask.title} onChange={(e) => handleTaskChange(mainTask.id, 'title', e.target.value)} className={mainTask.invalidFields.includes('title') ? 'invalid-field' : ''} /></div>
                                <div className="form-group"><label>Description</label><textarea value={mainTask.description} onChange={(e) => handleTaskChange(mainTask.id, 'description', e.target.value)} rows="3" /></div>
                                <div className="form-row">
                                    <div className="form-group"><label>Customer (Optional)</label><SearchableDropdown options={allCustomers} selectedValues={mainTask.customerId} onChange={(val) => handleCustomerChange(mainTask.id, val)} placeholder={areDropdownsDisabled ? "Loading..." : "Select Customer"} disabled={areDropdownsDisabled} allowClear={true} /></div>
                                    <div className="form-group"><label>Project (Optional)</label><SearchableDropdown options={mainTask.projectOptions} selectedValues={mainTask.projectId} onChange={(val) => handleTaskChange(mainTask.id, 'projectId', val)} placeholder={mainTask.isFetchingProjects ? "Loading..." : "Select Project"} disabled={areDropdownsDisabled || mainTask.isFetchingProjects} allowClear={true} /></div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group"><label>Category</label><SearchableDropdown options={allCategories} selectedValues={mainTask.category} onChange={(val) => handleTaskChange(mainTask.id, 'category', val)} placeholder={areDropdownsDisabled ? "Loading..." : "Select Category"} labelField="name" valueField="id" disabled={areDropdownsDisabled}/></div>
                                    <div className="form-group"><label>Priority</label><SearchableDropdown options={allPriorities} selectedValues={mainTask.priority} onChange={(val) => handleTaskChange(mainTask.id, 'priority', val)} placeholder={areDropdownsDisabled ? "Loading..." : "Select Priority"} labelField="name" valueField="id" disabled={areDropdownsDisabled}/></div>
                                </div>
                                <div className="form-group"><label>Assets (Optional)</label><SearchableDropdown options={allAssets} selectedValues={mainTask.assetIds} onChange={(val) => handleTaskChange(mainTask.id, 'assetIds', val)} placeholder={areDropdownsDisabled ? "Loading..." : "Select Assets"} isMultiSelect={true} disabled={areDropdownsDisabled}/></div>
                            </div>
                            <div className="form-side-panel">
                                <div className="assignment-section">
                                    <h4>Assignment</h4>
                                    <div className="form-group"><label>Assignee <span className="required-asterisk">*</span></label><SearchableDropdown options={assigneeOptions} selectedValues={mainTask.assigneeId} onChange={(val) => handleTaskChange(mainTask.id, 'assigneeId', val)} placeholder={areDropdownsDisabled ? "Loading..." : "Select Assignee"} error={mainTask.invalidFields.includes('assigneeId')} disabled={areDropdownsDisabled}/></div>
                                    <div className="form-group"><label>Reporting Manager <span className="required-asterisk">*</span></label><SearchableDropdown options={mainTask.reportingManagers} selectedValues={mainTask.reportingManagerId} onChange={(val) => handleTaskChange(mainTask.id, 'reportingManagerId', val)} placeholder={areDropdownsDisabled ? "Loading..." : "Select Manager"} error={mainTask.invalidFields.includes('reportingManagerId')} disabled={areDropdownsDisabled || !mainTask.assigneeId} /></div>
                                    <div className="form-group"><label>Tag Users (Optional)</label><SearchableDropdown options={taggableUsersForMain} selectedValues={mainTask.taggedUserIds} onChange={(val) => handleTaskChange(mainTask.id, 'taggedUserIds', val)} placeholder={areDropdownsDisabled ? "Loading..." : "Tag Users"} isMultiSelect={true} disabled={areDropdownsDisabled}/></div>
                                </div>
                                <div className="duedate-section">
                                    <h4>Due Date</h4>
                                    <div className="custom-duedate-inputs">
                                        <div className="date-picker-dmy"><select value={mainTaskDate.getDate()} onChange={(e) => handleDateTimeChange(mainTask.id, null, 'day', e.target.value)}>{Array.from({ length: getDaysInMonth(mainTaskDate.getFullYear(), mainTaskDate.getMonth() + 1) }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}</select><select value={mainTaskDate.getMonth() + 1} onChange={(e) => handleDateTimeChange(mainTask.id, null, 'month', e.target.value)}>{allMonths.map(m => <option key={m.value} value={m.value}>{m.name}</option>)}</select><select value={mainTaskDate.getFullYear()} onChange={(e) => handleDateTimeChange(mainTask.id, null, 'year', e.target.value)}>{years.map(y => <option key={y} value={y}>{y}</option>)}</select></div>
                                        <div className="time-picker-24h"><select value={pad(mainTaskDate.getHours())} onChange={(e) => handleDateTimeChange(mainTask.id, null, 'hour', e.target.value)}>{allHours.map(h => <option key={h} value={h}>{h}</option>)}</select><span>:</span><select value={pad(mainTaskDate.getMinutes())} onChange={(e) => handleDateTimeChange(mainTask.id, null, 'minute', e.target.value)}>{allMinutes.map(m => <option key={m} value={m}>{m}</option>)}</select></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {mainTask.subTasks.length > 0 && (
                            <div className="template-subtasks-section">
                                <h4>Subtasks</h4>
                                {mainTask.subTasks.map((subTask, subIndex) => {
                                    const subTaskDate = new Date(subTask.dueDate);
                                    const taggableUsersForSub = allUsers.filter(u => String(u.id) !== String(subTask.assigneeId));
                                    return (
                                    <div key={subTask.id} className="subtask-card">
                                        <div className="subtask-card-header"><h5>Subtask #{subIndex + 1}</h5></div>
                                        <div className="subtask-card-body">
                                            <div className="form-group"><label>Title <span className="required-asterisk">*</span></label><input type="text" value={subTask.title} onChange={(e) => handleSubtaskChange(mainTask.id, subTask.id, 'title', e.target.value)} className={subTask.invalidFields.includes('title') ? 'invalid-field' : ''} /></div>
                                            <div className="form-group"><label>Description</label><textarea value={subTask.description} onChange={(e) => handleSubtaskChange(mainTask.id, subTask.id, 'description', e.target.value)} rows="2" /></div>
                                            <div className="form-row">
                                                <div className="form-group"><label>Assignee <span className="required-asterisk">*</span></label><SearchableDropdown options={assigneeOptions} selectedValues={subTask.assigneeId} onChange={(val) => handleSubtaskChange(mainTask.id, subTask.id, 'assigneeId', val)} placeholder={areDropdownsDisabled ? "Loading..." : "Select Assignee"} error={subTask.invalidFields.includes('assigneeId')} disabled={areDropdownsDisabled}/></div>
                                                <div className="form-group"><label>Reporting Manager <span className="required-asterisk">*</span></label><SearchableDropdown options={subTask.reportingManagers} selectedValues={subTask.reportingManagerId} onChange={(val) => handleSubtaskChange(mainTask.id, subTask.id, 'reportingManagerId', val)} placeholder={areDropdownsDisabled ? "Loading..." : "Select Manager"} error={subTask.invalidFields.includes('reportingManagerId')} disabled={areDropdownsDisabled || !subTask.assigneeId}/></div>
                                            </div>
                                            <div className="form-row">
                                                <div className="form-group"><label>Status</label><SearchableDropdown options={allStatuses} selectedValues={subTask.status} onChange={(val) => handleSubtaskChange(mainTask.id, subTask.id, 'status', val)} placeholder={areDropdownsDisabled ? "Loading..." : "Select Status"} labelField="name" valueField="id" disabled={areDropdownsDisabled}/></div>
                                                <div className="form-group"><label>Priority</label><SearchableDropdown options={allPriorities} selectedValues={subTask.priority} onChange={(val) => handleSubtaskChange(mainTask.id, subTask.id, 'priority', val)} placeholder={areDropdownsDisabled ? "Loading..." : "Select Priority"} labelField="name" valueField="id" disabled={areDropdownsDisabled}/></div>
                                            </div>
                                            <div className="form-group"><label>Tag Users (Optional)</label><SearchableDropdown options={taggableUsersForSub} selectedValues={subTask.taggedUserIds} onChange={(val) => handleSubtaskChange(mainTask.id, subTask.id, 'taggedUserIds', val)} placeholder={areDropdownsDisabled ? "Loading..." : "Tag Users"} isMultiSelect={true} disabled={areDropdownsDisabled}/></div>
                                            <div className="form-group">
                                                <label>Due Date</label>
                                                <div className="custom-duedate-inputs">
                                                    <div className="date-picker-dmy"><select value={subTaskDate.getDate()} onChange={(e) => handleDateTimeChange(mainTask.id, subTask.id, 'day', e.target.value)}>{Array.from({ length: getDaysInMonth(subTaskDate.getFullYear(), subTaskDate.getMonth() + 1) }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}</select><select value={subTaskDate.getMonth() + 1} onChange={(e) => handleDateTimeChange(mainTask.id, subTask.id, 'month', e.target.value)}>{allMonths.map(m => <option key={m.value} value={m.value}>{m.name}</option>)}</select><select value={subTaskDate.getFullYear()} onChange={(e) => handleDateTimeChange(mainTask.id, subTask.id, 'year', e.target.value)}>{years.map(y => <option key={y} value={y}>{y}</option>)}</select></div>
                                                    <div className="time-picker-24h"><select value={pad(subTaskDate.getHours())} onChange={(e) => handleDateTimeChange(mainTask.id, subTask.id, 'hour', e.target.value)}>{allHours.map(h => <option key={h} value={h}>{h}</option>)}</select><span>:</span><select value={pad(subTaskDate.getMinutes())} onChange={(e) => handleDateTimeChange(mainTask.id, subTask.id, 'minute', e.target.value)}>{allMinutes.map(m => <option key={m} value={m}>{m}</option>)}</select></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    )})}
                            </div>
                        )}
                    </div>
                )})}

                <div className="form-actions">
                    <button type="button" onClick={handleSaveAsDraft} className="secondary-task-btn" disabled={isSubmitting || isSavingDraft || isFetchingMasterData}>
                        {isSavingDraft ? 'Saving...' : 'Save as Draft'}
                    </button>
                    <button type="button" onClick={handleSubmit} className="create-task-btn" disabled={isSubmitting || isSavingDraft || isFetchingMasterData}>
                        {isSubmitting ? 'Creating...' : 'Create All Tasks'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CreateTasksFromTemplatePage;